import  { useMemo, useCallback, useEffect, useState } from 'react';
import DataEditor, {
  type GridColumn,
  type GridCell,
  GridCellKind,
  type Item,
  type Theme,
  CompactSelection,
  type DrawHeaderCallback,
  type Rectangle,
} from '@glideapps/glide-data-grid';
import '@glideapps/glide-data-grid/dist/index.css';
import type { GridColumnDef } from './grid-columns';
import type { SortState, FilterRule } from './grid-columns';
import type { SfdcUser } from '../event-manager-client';
import { applyFilterRules, sortRows, cellDisplayValue } from './grid-data';

// ── Theme ────────────────────────────────────────────────────────────────────

const lightTheme: Partial<Theme> = {
  bgCell: '#ffffff',
  bgHeader: '#f8f9fa',
  bgHeaderHasFocus: '#f0f1f3',
  bgHeaderHovered: '#f0f1f3',
  borderColor: '#e2e4e8',
  textDark: '#1a1c23',
  textHeader: '#6e7381',
  accentColor: '#4f6bed',
  accentLight: '#eff2fd',
  bgBubble: '#eff2fd',
  textBubble: '#4f6bed',
};

const darkTheme: Partial<Theme> = {
  bgCell: '#0f1117',
  bgHeader: '#1a1c23',
  bgHeaderHasFocus: '#23262f',
  bgHeaderHovered: '#23262f',
  borderColor: '#3d4150',
  textDark: '#e2e4e8',
  textHeader: '#8b8f9a',
  accentColor: '#4f6bed',
  accentLight: '#1e2447',
  bgBubble: '#1e2447',
  textBubble: '#7b93f5',
};

// ── Dark mode detection ─────────────────────────────────────────────────────

function useIsDarkMode(): boolean {
  const [dark, setDark] = useState(() =>
    document.documentElement.classList.contains('dark'),
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return dark;
}

// ── Sort arrow drawing ──────────────────────────────────────────────────────

function drawSortArrow(ctx: CanvasRenderingContext2D, rect: Rectangle, dir: 'asc' | 'desc', color: string) {
  const arrowSize = 4;
  const x = rect.x + rect.width - 14;
  const y = rect.y + rect.height / 2;

  ctx.save();
  ctx.fillStyle = color;
  ctx.beginPath();
  if (dir === 'asc') {
    ctx.moveTo(x, y + 2);
    ctx.lineTo(x + arrowSize, y - 2);
    ctx.lineTo(x + arrowSize * 2, y + 2);
  } else {
    ctx.moveTo(x, y - 2);
    ctx.lineTo(x + arrowSize, y + 2);
    ctx.lineTo(x + arrowSize * 2, y - 2);
  }
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

// ── Props ────────────────────────────────────────────────────────────────────

interface GlideGridProps<T extends Record<string, any>> {
  data: T[];
  columns: GridColumnDef[];
  rowHeight: 28 | 34 | 48;
  onRowClick: (row: T) => void;
  selectedRowIndex: number | null;
  sfdcUsers: SfdcUser[];
  tableMap: Map<number, string>;
  sort: SortState;
  onSortChange: (sort: SortState) => void;
  filters: FilterRule[];
  columnWidths: Record<string, number>;
  onColumnWidthChange: (colId: string, width: number) => void;
  onColumnMoved: (startIndex: number, endIndex: number) => void;
  showSearch: boolean;
  searchValue: string;
  onSearchValueChange: (val: string) => void;
  onSearchClose: () => void;
}

// ── Component ───────────────────────────────────────────────────────────────

export function GlideGrid<T extends Record<string, any>>({
  data,
  columns: columnDefs,
  rowHeight,
  onRowClick,
  selectedRowIndex,
  sfdcUsers,
  tableMap,
  sort,
  onSortChange,
  filters,
  columnWidths,
  onColumnWidthChange,
  onColumnMoved,
  showSearch,
  searchValue,
  onSearchValueChange,
  onSearchClose,
}: GlideGridProps<T>) {
  const isDark = useIsDarkMode();
  const theme = isDark ? darkTheme : lightTheme;

  // SFDC user name lookup
  const userNameMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of sfdcUsers) m.set(u.userId, u.name);
    return m;
  }, [sfdcUsers]);

  // Filtered data
  const filteredData = useMemo(
    () => applyFilterRules(data, filters),
    [data, filters],
  );

  // Sorted data
  const sortedData = useMemo(
    () => sortRows(filteredData, sort, columnDefs),
    [filteredData, sort, columnDefs],
  );

  // Grid columns for DataEditor — use custom widths when available
  const gridColumns: GridColumn[] = useMemo(
    () => columnDefs.map(c => ({
      title: c.title,
      width: columnWidths[c.id] ?? c.width,
      id: c.id,
    })),
    [columnDefs, columnWidths],
  );

  // ── drawHeader — render sort indicator on canvas ────────────────────────

  const drawHeader: DrawHeaderCallback = useCallback(
    (args, drawContent) => {
      drawContent();
      const { columnIndex, ctx, rect, theme: headerTheme } = args;
      if (sort.colIndex === columnIndex && sort.dir) {
        drawSortArrow(ctx, rect, sort.dir, headerTheme.textHeader);
      }
      return true;
    },
    [sort],
  );

  // ── getCellContent ────────────────────────────────────────────────────────

  const getCellContent = useCallback(
    ([colIdx, rowIdx]: Item): GridCell => {
      const row = sortedData[rowIdx];
      if (!row) return { kind: GridCellKind.Text, data: '', displayData: '', allowOverlay: false };

      const colDef = columnDefs[colIdx];
      if (!colDef) return { kind: GridCellKind.Text, data: '', displayData: '', allowOverlay: false };

      const rawVal = row[colDef.field];

      // Boolean cells
      if (colDef.cellKind === 'boolean') {
        return {
          kind: GridCellKind.Boolean,
          data: rawVal === true,
          allowOverlay: false,
          readonly: true,
        };
      }

      // Bubble cells (account_type)
      if (colDef.cellKind === 'bubble') {
        const text = rawVal != null ? String(rawVal) : '';
        return {
          kind: GridCellKind.Bubble,
          data: text ? [text] : [],
          allowOverlay: false,
        };
      }

      // Text cells — apply formatFn or resolve special fields
      const display = cellDisplayValue(colDef, row, { tableMap, userNameMap });

      return {
        kind: GridCellKind.Text,
        data: display,
        displayData: display,
        allowOverlay: false,
      };
    },
    [sortedData, columnDefs, tableMap, userNameMap],
  );

  // ── Event handlers ────────────────────────────────────────────────────────

  const onHeaderClicked = useCallback(
    (colIdx: number) => {
      onSortChange(
        sort.colIndex !== colIdx
          ? { colIndex: colIdx, dir: 'asc' }
          : sort.dir === 'asc'
            ? { colIndex: colIdx, dir: 'desc' }
            : { colIndex: -1, dir: null },
      );
    },
    [sort, onSortChange],
  );

  const onCellClicked = useCallback(
    ([_col, rowIdx]: Item) => {
      const row = sortedData[rowIdx];
      if (row) onRowClick(row);
    },
    [sortedData, onRowClick],
  );

  const handleColumnResize = useCallback(
    (_column: GridColumn, newSize: number, colIndex: number) => {
      const colDef = columnDefs[colIndex];
      if (colDef) onColumnWidthChange(colDef.id, newSize);
    },
    [columnDefs, onColumnWidthChange],
  );

  // Highlight selected row
  const gridSelection = useMemo(() => {
    if (selectedRowIndex == null) return undefined;
    return {
      rows: CompactSelection.fromSingleSelection(selectedRowIndex),
      columns: CompactSelection.empty(),
    };
  }, [selectedRowIndex]);

  // Height: header + all rows, filling available viewport space
  const headerH = 34;
  const contentH = headerH + sortedData.length * rowHeight;
  const maxH = typeof window !== 'undefined' ? window.innerHeight - 260 : 600;
  const gridHeight = Math.min(contentH, Math.max(maxH, 300));

  return (
    <div
      className="rounded-lg border border-nightshade-200 dark:border-nightshade-700 overflow-hidden"
      style={{ overscrollBehaviorX: 'contain' }}
    >
      <DataEditor
        columns={gridColumns}
        rows={sortedData.length}
        getCellContent={getCellContent}
        getCellsForSelection={true}
        rowHeight={rowHeight}
        headerHeight={headerH}
        theme={theme}
        rowMarkers="number"
        rangeSelect="rect"
        columnSelect="none"
        rowSelect="none"
        smoothScrollX
        smoothScrollY
        onHeaderClicked={onHeaderClicked}
        onCellClicked={onCellClicked}
        onColumnResize={handleColumnResize}
        onColumnMoved={onColumnMoved}
        drawHeader={drawHeader}
        gridSelection={gridSelection}
        showSearch={showSearch}
        searchValue={searchValue}
        onSearchValueChange={onSearchValueChange}
        onSearchClose={onSearchClose}
        width="100%"
        height={gridHeight}
        className="gdg-grid"
      />
      <div id="portal" />
    </div>
  );
}
